import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../../utils/notificationUtils';
import enrollmentService from '../../services/enrollmentService';
import { localizeClassItem } from '../../config/classUiConfig';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Chip,
    Button,
    CircularProgress,
    Alert,
    Grid,
    Tooltip,
    IconButton
} from '@mui/material';
import {
    AccessTime as AccessTimeIcon,
    LocationOn as LocationIcon,
    CalendarToday as CalendarIcon,
    Info as InfoIcon
} from '@mui/icons-material';

function MyWaitlist() {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';
    const navigate = useNavigate();
    const { showSuccess, showError } = useNotifications();
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWaitlistEntries();
    }, []);

    const getLocalizedClassName = (title) => {
        if (!title) return title;
        return localizeClassItem({ title }, i18n.language).title;
    };

    const fetchWaitlistEntries = async () => {
        try {
            setLoading(true);
            const entries = await enrollmentService.getUserWaitlistEntries();
            const pendingEntries = entries.filter(entry =>
                entry.status === 'waiting' || entry.status === 'pending' || entry.status === 'offered'
            );
            setWaitlistEntries(pendingEntries);
        } catch (err) {
            setError(err.message || t('waitlist.loadFailed'));
            showError(t('waitlist.loadFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleLeaveWaitlist = async (classId) => {
        try {
            await enrollmentService.leaveWaitlist(classId);
            showSuccess(t('waitlist.leaveSuccess'));
            fetchWaitlistEntries();
        } catch (err) {
            showError(err.message || t('waitlist.leaveFailed'));
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting':
                return 'warning';
            case 'offered':
                return 'info';
            case 'accepted':
                return 'success';
            case 'declined':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusLabel = (status) => {
        const key = `waitlist.status.${status}`;
        const translated = t(key);
        return translated !== key ? translated : status.toUpperCase();
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString(dateLocale, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatWaitTime = (days) => {
        if (!days) return t('waitlist.calculating');
        if (days < 1) return t('waitlist.lessThanDay');
        if (days === 1) return t('waitlist.oneDay');
        return t('waitlist.days', { count: Math.ceil(days) });
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mb: 2 }}>
                {error}
            </Alert>
        );
    }

    if (waitlistEntries.length === 0) {
        return (
            <Box textAlign="center" py={4}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    {t('waitlist.emptyTitle')}
                </Typography>
                <Typography color="textSecondary">
                    {t('waitlist.emptyDesc')}
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => navigate('/classes')}
                    sx={{ mt: 2 }}
                >
                    {t('waitlist.browseClasses')}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                {t('waitlist.title')}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                {t('waitlist.subtitle')}
            </Typography>
            <Grid container spacing={3}>
                {waitlistEntries.map((entry) => (
                    <Grid item xs={12} key={entry.id}>
                        <Card>
                            <CardContent>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={8}>
                                        <Typography variant="h6" gutterBottom>
                                            {getLocalizedClassName(entry.class_title)}
                                        </Typography>
                                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Chip
                                                label={getStatusLabel(entry.status)}
                                                color={getStatusColor(entry.status)}
                                                size="small"
                                            />
                                            {entry.status === 'waiting' && (
                                                <Chip
                                                    icon={<AccessTimeIcon />}
                                                    label={t('waitlist.position', { position: entry.position })}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            )}
                                        </Box>
                                        <Grid container spacing={2} sx={{ mt: 1 }}>
                                            <Grid item xs={12} sm={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <CalendarIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {formatDate(entry.start_date)} - {formatDate(entry.end_date)}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    <LocationIcon fontSize="small" color="action" />
                                                    <Typography variant="body2">
                                                        {entry.location_details}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            {entry.status === 'waiting' && (
                                                <Grid item xs={12}>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <AccessTimeIcon fontSize="small" color="action" />
                                                        <Typography variant="body2">
                                                            {t('waitlist.estimatedWait', { time: formatWaitTime(entry.estimated_wait_time) })}
                                                        </Typography>
                                                        <Tooltip title={t('waitlist.waitTooltip')}>
                                                            <IconButton size="small">
                                                                <InfoIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box display="flex" flexDirection="column" gap={1} alignItems="flex-end">
                                            {entry.status === 'waiting' && (
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    onClick={() => handleLeaveWaitlist(entry.class_id)}
                                                >
                                                    {t('waitlist.leave')}
                                                </Button>
                                            )}
                                            {entry.status === 'offered' && (
                                                <Box>
                                                    <Typography variant="body2" color="info.main" gutterBottom>
                                                        {t('waitlist.spotAvailable')}
                                                    </Typography>
                                                    <Box display="flex" gap={1}>
                                                        <Button
                                                            variant="contained"
                                                            color="success"
                                                            onClick={() => navigate(`/classes/${entry.class_id}`)}
                                                        >
                                                            {t('waitlist.accept')}
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={() => handleLeaveWaitlist(entry.class_id)}
                                                        >
                                                            {t('waitlist.decline')}
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            )}
                                            <Button
                                                variant="text"
                                                onClick={() => navigate(`/classes/${entry.class_id}`)}
                                            >
                                                {t('waitlist.viewDetails')}
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
}

export default MyWaitlist;
